import RunBubble from './templates/RunBubble';
import { TemplateRunsProvider } from './templates/TemplateRunsContext';
import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { checkAuthAndRedirect } from '@monorepo/auth/Actions';
import { RequireAuth } from '@monorepo/auth/components/RequireAuth';
import DebugPanel from '@monorepo/common/debug/DebugPanel';
import { AgentRunProvider } from './agent/AgentRunContext';
import { TikTokProvider } from './context/TikTokContext';
import { InstagramProvider } from './context/InstagramContext';
import { FacebookProvider } from './context/FacebookContext';
import { ThreadsProvider } from './context/ThreadsContext';
import { TwitterProvider } from './context/TwitterContext';
import { BrowserSessionProvider } from './context/BrowserSessionContext';
import { RenderJobsProvider } from './context/RenderJobsContext';
import { RenderJobsToast } from './components/RenderJobsToast';
import DashboardLayout from '@monorepo/layout/components/DashboardLayout';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import TikTokCallback from './pages/TikTokCallback';
import MetaCallback from './pages/MetaCallback';
import ThreadsCallback from './pages/ThreadsCallback';
import ConnectAccounts from './pages/ConnectAccounts';
import PostTimeline from './pages/PostTimeline';
import TwitterCallback from './pages/TwitterCallback';
import OAuthSuccessPage from '@monorepo/auth/pages/OAuthSuccessPage';

// ── Code splitting ──────────────────────────────────────────────────────────
// lazyRoute, not React.lazy: chunk names carry a content hash, so a tab left
// open across a deploy asks for a chunk that no longer exists and the route
// goes blank. See shared/common/utils/lazyRoute.js — it retries once, then
// reloads once, guarded against a loop.
// Everything below is loaded on demand. Before this, one 5MB bundle was
// downloaded and parsed on every visit, including VideoStudio (6,600 lines
// plus a canvas renderer), ContentEditor (Fabric.js) and BrowserLiveView
// (noVNC) — three things most sessions never open, in front of a dashboard
// on a phone.
//
// What stays EAGER is the set a cold start actually lands on: Login,
// Dashboard, ConnectAccounts, PostTimeline and the OAuth callbacks. Lazily
// loading a route the user is already navigating to just adds a spinner.
//
// The callbacks in particular must NOT be lazy: they run during an OAuth
// redirect, which is exactly when the network is least predictable, and a
// failed chunk fetch there strands someone mid-connect with a code they
// cannot re-use.
const TikTokAccountOverview = lazyRoute(() => import('./pages/TikTokAccountOverview'));
const InstagramAccountOverview = lazyRoute(() => import('./pages/InstagramAccountOverview'));
const FacebookAccountOverview = lazyRoute(() => import('./pages/FacebookAccountOverview'));
const ThreadsAccountOverview = lazyRoute(() => import('./pages/ThreadsAccountOverview'));
const TwitterAccountOverview = lazyRoute(() => import('./pages/TwitterAccountOverview'));
const UploadVideo = lazyRoute(() => import('./pages/UploadVideo'));
// Lazy: a cold start lands on the dashboard, not here, and the composer pulls
// in the media picker with it.
const ComposePost = lazyRoute(() => import('./compose/ComposePost'));
const Drafts = lazyRoute(() => import('./pages/Drafts'));
// The editors are the content. On a phone they get the whole screen -- their
// own action bars are better placed to do the job the app tab bar would
// otherwise be doing badly from underneath them. Declared once because the
// agent dock hides on the same set: a floating control over a canvas sits on
// top of that action bar.
const IMMERSIVE_ROUTES = ['/media/editor', '/media/video-studio', '/media/templates'];

// Lazy: the assistant is not on a cold-start path, and its page pulls in
// nothing the dashboard needs.
const Assistant = lazyRoute(() => import('./pages/Assistant'));
// Staging only, and written as a direct comparison for the reason the debug
// panel's DEBUG_ENABLED is: Vite substitutes a string literal at build time,
// so this folds to `false` in production and the import() -- and the whole
// chunk with it -- is dropped rather than shipped inert.
const STAGING = import.meta.env.VITE_ENVIRONMENT === 'staging';
const ReleaseChecklist = STAGING
  ? lazyRoute(() => import('./release/ReleaseChecklist'))
  : null;
// The same checklist, inside the staging debug panel, so a reviewer can tick
// the thing they just did without leaving the screen they did it on. Lazy and
// staging-gated for the same reason as the page: the catalogue must not reach
// a production bundle.
const DEBUG_TABS = STAGING
  ? [{ id: 'checklist', label: 'CHECKLIST',
       Component: React.lazy(() => import('./release/ChecklistPanel')) }]
  : [];
const MediaLibrary = lazyRoute(() => import('./pages/MediaLibrary'));
const ContentEditor = lazyRoute(() => import('./pages/ContentEditor'));
// The HTML template editor: designs as HTML + Tailwind with a slot schema,
// generated and edited by the model, edited by hand with no model. Its own
// route and folder (src/templates) -- it replaces nothing in ContentEditor.
const TemplateStudio = lazyRoute(() => import('./templates/TemplateStudio'));
const VideoStudio = lazyRoute(() => import('./pages/VideoStudio'));
const BrowserSessions = lazyRoute(() => import('./pages/BrowserSessions'));
const BrowserLiveView = lazyRoute(() => import('./pages/BrowserLiveView'));
const EmailDomains = lazyRoute(() => import('./pages/EmailDomains'));
const PhoneNumbers = lazyRoute(() => import('./pages/PhoneNumbers'));
const NotificationSettings = lazyRoute(() => import('./pages/NotificationSettings'));
const DesktopApp = lazyRoute(() => import('./pages/DesktopApp'));
const AdminDashboard = lazyRoute(() => import('./pages/admin/AdminDashboard'));
import { syncSubscription } from '@monorepo/common/utils/push';
import { lazyRoute } from '@monorepo/common/utils/lazyRoute';
import { RouteErrorBoundary } from '@monorepo/common/components/RouteErrorBoundary';
import {
  HomeIcon,
  LinkIcon,
  PhotoIcon,
  PaintBrushIcon,
  FilmIcon,
  Cog6ToothIcon,
  QueueListIcon,
  BellAlertIcon,
  ClipboardDocumentCheckIcon,
  ComputerDesktopIcon,
  ArrowDownTrayIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const navigation = [
  {
    name: 'Home',
    icon: HomeIcon,
    children: [
      { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, permission: 'oddsignals_access' },
      // Create Post is deliberately NOT here. The sidebar is a list of
      // PLACES; making a post is an action, and an action in a directory of
      // destinations reads as somewhere to go rather than something to do.
      // It is the primary button on the dashboard, and every account page
      // deep-links to it with that account preselected — both of which put it
      // where the decision to post is actually made. The /compose route is
      // unchanged, so old links still work.
      //
      // Posts is ONE entry, not three. Published, drafts and (later) scheduled are the
      // same object at different points in its life, so they are tabs inside
      // /posts rather than separate destinations — two sidebar entries made a
      // post that is not published yet look like a different kind of thing
      // kept somewhere else. /drafts is still a real URL and still works.
      { name: 'Posts', href: '/posts', icon: QueueListIcon, permission: 'oddsignals_access' },
      { name: 'Media Library', href: '/media', icon: PhotoIcon, permission: 'manage_media_library' },
      { name: 'Content Editor', href: '/media/editor', icon: PaintBrushIcon, permission: 'manage_media_library' },
      { name: 'Templates', href: '/media/templates', icon: SparklesIcon, permission: 'manage_media_library' },
      { name: 'Video Studio', href: '/media/video-studio', icon: FilmIcon, permission: 'manage_media_library' },
      { name: 'Connect Account', href: '/connect', icon: LinkIcon, permission: 'oddsignals_access' },
      // Ultimate only, and the permission is the gate — nobody on Free or Pro
      // sees this. It had no nav entry at all before, which meant the tier's
      // headline feature was reachable only by typing /browser: not hidden,
      // just undiscoverable by the people it is sold to.
      // The app-surface Assistant is HIDDEN for now, nav entry and dock both:
      // the templates editor is growing its own chat, and the dock's
      // bottom-right corner is where the template runs report from. Two
      // floating launchers in one corner is "drag one of them", which is a
      // poor first thirty seconds.
      //
      // The route survives, so /assistant still opens for anyone who knows
      // it is there and holds `ai_control` — nothing has been deleted, and
      // putting the entry back is this block.
      {
        name: 'Browser Sessions', href: '/browser', icon: ComputerDesktopIcon,
        permission: 'manage_browser_sessions',
      },
      // Pro and up, which is a WIDER audience than Browser Sessions above:
      // downloading the app and using persistent profiles are two
      // permissions. It had lived as a card inside /browser, so the only
      // people who could see it were the ones who already had it.
      {
        name: 'Desktop app', href: '/settings/desktop', icon: ArrowDownTrayIcon,
        permission: 'download_desktop_app',
      },
      { name: 'Notifications', href: '/settings/notifications', icon: BellAlertIcon, permission: 'oddsignals_access' },
      // Only on staging, where promoting to production is the next thing that
      // happens. In production it would be a list of things to check about a
      // deployment that has already shipped.
      ...(STAGING ? [{
        name: 'Release checklist', href: '/release-check',
        icon: ClipboardDocumentCheckIcon, permission: 'oddsignals_access',
      }] : []),
    ]
  },
  // Single entry point into the admin area. Clicking it navigates to /admin,
  // which mounts AdminDashboard with its OWN admin-only sidebar (see
  // pages/admin/AdminDashboard.jsx). Gated by the admin-dashboard permission.
  {
    name: 'Admin',
    icon: Cog6ToothIcon,
    href: '/admin',
    permission: 'view_admin_dashboard',
  },
];

// Filter the navigation tree down to the items the current user is allowed to
// see. An item is visible when it has no `permission` field, or when the user's
// permissions include that permission. Because the backend expands the 'admin'
// superuser role into all child permissions in the check-auth response, a plain
// `includes` check works for admins too; we keep an explicit 'admin' fallback in
// case the response ever only returns the top-level role name.
const filterNavigationByPermission = (items, permissions = []) => {
  const isAdmin = permissions.includes('admin');
  const allowed = (item) =>
    !item.permission || isAdmin || permissions.includes(item.permission);

  return items
    .filter(allowed)
    .map((item) => ({
      ...item,
      children: item.children
        ? item.children.filter(allowed)
        : undefined,
    }))
    // Drop parent groups that ended up with no visible children.
    .filter((item) => !item.children || item.children.length > 0);
};

// In-shell route fallback. Not the full-screen one below: this renders where
// the page content goes, inside the layout, so the chrome does not flash away
// and back on every navigation to a split route.
const RouteFallback = () => (
  <div className="flex h-full items-center justify-center bg-canvas" role="status" aria-live="polite">
    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-ink" />
    <span className="sr-only">Loading</span>
  </div>
);

// Full-screen loader, for before the shell itself exists (initial auth check).
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-canvas">
    <div className="flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ink"></div>
    </div>
  </div>
);

// Guard a route by a required permission. Non-admins lacking the permission are
// redirected back to the dashboard. Admins (expanded perms or the 'admin' role)
// pass through.
const RequirePermission = ({ permission, children }) => {
  const permissions = useSelector((state) => state.auth?.permissions || []);
  const allowed =
    permissions.includes('admin') || permissions.includes(permission);
  return allowed ? children : <Navigate to="/dashboard" replace />;
};

const AuthenticatedApp = () => {
  const permissions = useSelector((state) => state.auth?.permissions || []);
  const filteredNavigation = filterNavigationByPermission(navigation, permissions);

  // Reconcile the browser's push subscription with the server's copy, once per
  // session. Push services rotate endpoints without telling the page, and the
  // service worker's own pushsubscriptionchange handler has no auth token to
  // report one with — so this authenticated pass is the guarantee. It is a
  // no-op unless notifications are already granted, so it never prompts.
  useEffect(() => {
    syncSubscription().catch(() => {});
  }, []);

  return (
    // Above the inner <Routes>, so a run keeps going -- and keeps being
    // watchable -- while you move around the app looking at what it changed.
    //
    // The template runs need the same, for a sharper reason: generation takes
    // a minute or two, and until this the composer polled in its own
    // component — so leaving the page lost the run, the result arrived to
    // nobody, and the template was never saved.
    <AgentRunProvider>
    <TemplateRunsProvider>
    <DashboardLayout
      navigation={filteredNavigation}
      title="OddSignals"
      brandName="oddsignals"
      logo={null}
      searchBar={null}
      userMenuOptions={{ showSpotify: false, showBlockedArtists: false }}
      immersiveRoutes={IMMERSIVE_ROUTES}
      // The corner now reports TEMPLATE runs rather than the agent, which is
      // why the dock is hidden: two floating launchers in one corner is
      // "drag one of them". Positioned inside <main> for the reason this
      // prop exists — the phone tab bar is a flex sibling, not an overlay,
      // so anything fixed to the viewport sits on top of "More".
      //
      // Hidden on the immersive editors, whose own bottom bar owns that
      // space — and /media/templates is one of them, which is also the page
      // that lists these runs in full. So the one list covers both reasons.
      // This used to pass that list with that path appended, which read as a
      // second rule and was a no-op: the check asserting the bubble's
      // absence there passed with the append removed.
      overlay={<RunBubble hideOn={IMMERSIVE_ROUTES} />}
    >
      {/* The boundary sits INSIDE DashboardLayout, not around it: the shell —
          sidebar, header, phone tab bar — stays painted while a route chunk
          downloads, so a slow connection shows the app with a loading pane
          rather than a blank screen that looks like a crash. */}
      <RouteErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route
          path="dashboard"
          element={<Dashboard />}
        />
        <Route
          path="connect"
          element={<ConnectAccounts />}
        />
        <Route
          path="compose"
          element={<ComposePost />}
        />
        <Route
          path="assistant"
          element={
            <RequirePermission permission="ai_control">
              <Assistant />
            </RequirePermission>
          }
        />
        <Route
          path="drafts"
          element={<Drafts />}
        />
        {STAGING && (
          <Route
            path="release-check"
            element={<ReleaseChecklist />}
          />
        )}

        <Route
          path="posts"
          element={<PostTimeline />}
        />
        <Route
          path="settings/notifications"
          element={<NotificationSettings />}
        />
        <Route
          path="settings/desktop"
          element={
            <RequirePermission permission="download_desktop_app">
              <DesktopApp />
            </RequirePermission>
          }
        />
        <Route
          path="account/:accountId"
          element={<TikTokAccountOverview />}
        />
        <Route
          path="ig-account/:accountId"
          element={<InstagramAccountOverview />}
        />
        <Route
          path="fb-page/:accountId"
          element={<FacebookAccountOverview />}
        />
        <Route
          path="threads-account/:accountId"
          element={<ThreadsAccountOverview />}
        />
        <Route
          path="twitter-account/:accountId"
          element={<TwitterAccountOverview />}
        />
        <Route
          path="media"
          element={<MediaLibrary />}
        />
        <Route
          path="media/editor"
          element={<ContentEditor />}
        />
        <Route
          path="media/templates"
          element={<TemplateStudio />}
        />
        <Route
          path="media/video-studio"
          element={<VideoStudio />}
        />
        <Route
          path="account/:accountId/upload"
          element={<UploadVideo />}
        />
        {/* Browser sessions are the ULTIMATE tier, and `manage_browser_sessions`
            is the permission that tier grants. The guard is here rather than
            only on the nav entry because a route with no nav entry is not
            hidden — it is one typed URL away, and these four were reachable by
            anyone signed in. The backend refuses them too; this is what keeps
            the app from rendering an empty page full of 403s instead of saying
            where it sent you. */}
        <Route
          path="browser"
          element={(
            <RequirePermission permission="manage_browser_sessions">
              <BrowserSessions />
            </RequirePermission>
          )}
        />
        <Route
          path="browser/email-domains"
          element={(
            <RequirePermission permission="manage_browser_sessions">
              <EmailDomains />
            </RequirePermission>
          )}
        />
        <Route
          path="browser/phone-numbers"
          element={(
            <RequirePermission permission="manage_browser_sessions">
              <PhoneNumbers />
            </RequirePermission>
          )}
        />
        <Route
          path="browser/:id"
          element={(
            <RequirePermission permission="manage_browser_sessions">
              <BrowserLiveView />
            </RequirePermission>
          )}
        />
        <Route
          index
          element={<Navigate to="/dashboard" />}
        />
        <Route
          path="*"
          element={<Navigate to="/dashboard" />}
        />
      </Routes>
      </Suspense>
      </RouteErrorBoundary>
    </DashboardLayout>
    </TemplateRunsProvider>
    </AgentRunProvider>
  );
};

function App() {
  const dispatch = useDispatch();
  const [isInitializing, setIsInitializing] = useState(true);
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);

  // Check authentication status on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await dispatch(checkAuthAndRedirect());
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeAuth();
  }, [dispatch]);

  if (isInitializing) {
    return <LoadingFallback />;
  }

  return (
    <BrowserRouter>
      <RenderJobsProvider>
      <TikTokProvider>
      <InstagramProvider>
      <FacebookProvider>
      <ThreadsProvider>
      <TwitterProvider>
      <BrowserSessionProvider>
        <RenderJobsToast />
        <Routes>
          {/* Login route */}
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" />
              ) : (
                <Login />
              )
            }
          />

          {/* Signup, reached from an invite email. Eager for the same reason
              the OAuth callbacks are: it is a cold start from a link, which
              is the worst moment to wait on a second round trip. */}
          <Route
            path="/signup"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" />
              ) : (
                <Signup />
              )
            }
          />

          {/* TikTok OAuth callback - no auth required */}
          <Route
            path="/auth/tiktok/callback"
            element={<TikTokCallback />}
          />

          {/* Instagram OAuth callback - no auth required */}
          <Route
            path="/auth/instagram/callback"
            element={<MetaCallback />}
          />

          {/* Facebook OAuth callback - no auth required */}
          <Route
            path="/auth/facebook/callback"
            element={<MetaCallback />}
          />

          {/* Threads OAuth callback - no auth required */}
          <Route
            path="/auth/threads/callback"
            element={<ThreadsCallback />}
          />

          {/* Twitter OAuth callback - no auth required */}
          <Route
            path="/auth/twitter/callback"
            element={<TwitterCallback />}
          />

          {/* OAuth success callback */}
          <Route
            path="/auth/oauth-success"
            element={<OAuthSuccessPage defaultRedirect="/dashboard" />}
          />

          {/* Admin area, mounted OUTSIDE AuthenticatedApp's DashboardLayout.
              AdminDashboard renders its own layout with an admin-only
              sidebar; nesting it inside the app's layout stacked two headers
              and two hamburger buttons on top of each other and left the
              phone tab bar advertising app routes from underneath an admin
              page — about 110px of chrome before any content, on every admin
              screen. Route ranking puts this ahead of the catch-all below
              regardless of order, and the admin routes stay relative to
              /admin exactly as before. */}
          <Route
            path="/admin/*"
            element={
              <RequireAuth requiredPermissions={['oddsignals_access']}>
                <RequirePermission permission="view_admin_dashboard">
                  {/* Its own boundary: the admin area renders its own layout
                      outside AuthenticatedApp, so it is not covered by the
                      Suspense inside that one. */}
                  <RouteErrorBoundary>
                    <Suspense fallback={<LoadingFallback />}>
                      <AdminDashboard />
                    </Suspense>
                  </RouteErrorBoundary>
                </RequirePermission>
              </RequireAuth>
            }
          />

          {/* All other authenticated routes use the shared dashboard layout */}
          <Route
            path="/*"
            element={
              <RequireAuth requiredPermissions={['oddsignals_access']}>
                <AuthenticatedApp />
              </RequireAuth>
            }
          />
        </Routes>
      </BrowserSessionProvider>
      </TwitterProvider>
      </ThreadsProvider>
      </FacebookProvider>
      </InstagramProvider>
      </TikTokProvider>
      </RenderJobsProvider>
      {/* Staging only -- renders nothing in production. */}
      <DebugPanel tabs={DEBUG_TABS} />
    </BrowserRouter>
  );
}

export default App;
